/**
 * ReportFab.jsx
 *
 * Floating Action Button (bottom-right) that opens the report dialog.
 * Pre-captures a screenshot before opening to minimize the dialog-visible period.
 *
 * Renders only when the user is authenticated.
 */

import React, { useState } from 'react';
import { IconButton, Tooltip, Snackbar, Alert } from '@mui/material';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { useReportContext } from '../../hooks/useReportContext';
import ReportDialog from './ReportDialog';

const ReportFab = ({ sx = {}, tooltipPlacement = 'top' }) => {
  const { detectCurrentArea } = useReportContext();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [preSnapshot, setPreSnapshot] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const handleFabClick = async () => {
    if (capturing) return;

    setCapturing(true);
    let blob = null;

    // Attempt pre-snapshot before dialog blocks the screen
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scale: 0.75,
        // Don't capture the FAB itself to avoid confusion
        ignoreElements: el => el.dataset && el.dataset.testid === 'report-fab',
      });
      blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
    } catch (err) {
      console.warn('[ReportFab] Pre-snapshot failed:', err.message);
    }

    setPreSnapshot(blob);
    setCapturing(false);
    setDialogOpen(true);
  };

  const handleSuccess = () => {
    setSuccessOpen(true);
    setPreSnapshot(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setPreSnapshot(null);
  };

  return (
    <>
      <Tooltip title="Envíanos feedback" placement={tooltipPlacement}>
        <IconButton
          size="small"
          onClick={handleFabClick}
          disabled={capturing}
          data-testid="report-fab"
          sx={{
            color: '#F59E0B',
            '&:hover': {
              color: '#D97706',
            },
            ...sx,
          }}
          aria-label="Envíanos feedback"
        >
          <LightbulbOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <ReportDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSuccess={handleSuccess}
        preSnapshot={preSnapshot}
        defaultArea={detectCurrentArea()}
      />

      <Snackbar
        open={successOpen}
        autoHideDuration={4000}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSuccessOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Recibido, gracias.
        </Alert>
      </Snackbar>
    </>
  );
};

export default ReportFab;
